import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import { HtmlReportRowActivityCount } from '../../domain/html-report-annotations';
import { TaskService } from '../task.service';

export type HtmlReportRowActivityCountLoadStatus =
   | 'IDLE'
   | 'LOADING'
   | 'LOADED'
   | 'ERROR';

export interface HtmlReportRowActivityCountState {
   status: HtmlReportRowActivityCountLoadStatus;
   countsByRowId: ReadonlyMap<string, HtmlReportRowActivityCount>;
   errorStatus: number | null;
}

type PendingRowActivityDelta = {
   commentCount: number;
   mentionCount: number;
};

const INITIAL_STATE: HtmlReportRowActivityCountState = {
   status: 'IDLE',
   countsByRowId: new Map<string, HtmlReportRowActivityCount>(),
   errorStatus: null
};

@Injectable({
   providedIn: 'root'
})
export class HtmlReportRowActivityCountStoreService {
   private readonly stateSubject =
      new BehaviorSubject<HtmlReportRowActivityCountState>(INITIAL_STATE);

   readonly state$ = this.stateSubject.asObservable();

   private activeTaskInstanceId: number | null = null;
   private loadingTaskInstanceId: number | null = null;
   private requestVersion = 0;

   private readonly pendingDeltas = new Map<string, PendingRowActivityDelta>();

   constructor(private taskService: TaskService) {}

   load(taskInstanceId: number): void {
      if (taskInstanceId <= 0) {
         this.reset();
         return;
      }

      if (this.loadingTaskInstanceId === taskInstanceId) {
         return;
      }

      if (this.activeTaskInstanceId !== taskInstanceId) {
         this.pendingDeltas.clear();
      }

      const requestVersion = ++this.requestVersion;

      this.activeTaskInstanceId = taskInstanceId;
      this.loadingTaskInstanceId = taskInstanceId;

      this.stateSubject.next({
         status: 'LOADING',
         countsByRowId: new Map<string, HtmlReportRowActivityCount>(),
         errorStatus: null
      });

      this.taskService.getHtmlReportRowActivityCounts(taskInstanceId).subscribe({
         next: (counts) => {
            if (requestVersion !== this.requestVersion) {
               return;
            }

            this.loadingTaskInstanceId = null;

            const countsByRowId = new Map<string, HtmlReportRowActivityCount>();

            for (const item of counts ?? []) {
               const rowId = item.targetRowId?.trim();

               if (!rowId) {
                  continue;
               }

               countsByRowId.set(rowId, {
                  targetRowId: rowId,
                  commentCount: Math.max(0, item.commentCount ?? 0),
                  mentionCount: Math.max(0, item.mentionCount ?? 0)
               });
            }

            this.applyPendingDeltas(countsByRowId);
            this.pendingDeltas.clear();

            this.stateSubject.next({
               status: 'LOADED',
               countsByRowId,
               errorStatus: null
            });
         },
         error: (error: HttpErrorResponse) => {
            if (requestVersion !== this.requestVersion) {
               return;
            }

            this.loadingTaskInstanceId = null;

            const countsByRowId = new Map<string, HtmlReportRowActivityCount>();

            this.applyPendingDeltas(countsByRowId);
            this.pendingDeltas.clear();

            this.stateSubject.next({
               status: 'ERROR',
               countsByRowId,
               errorStatus: error.status
            });
         }
      });
   }

   recordCommentAdded(
      taskInstanceId: number,
      targetRowId: string,
      mentionCount: number
   ): void {
      const rowId = targetRowId.trim();

      if (
         this.activeTaskInstanceId !== taskInstanceId ||
         !rowId ||
         !Number.isInteger(mentionCount) ||
         mentionCount < 0
      ) {
         return;
      }

      const currentState = this.stateSubject.value;
      const countsByRowId = new Map(currentState.countsByRowId);

      if (currentState.status === 'LOADING') {
         const currentDelta = this.pendingDeltas.get(rowId) ?? {
            commentCount: 0,
            mentionCount: 0
         };

         this.pendingDeltas.set(rowId, {
            commentCount: currentDelta.commentCount + 1,
            mentionCount: currentDelta.mentionCount + mentionCount
         });
      }

      const currentCount = countsByRowId.get(rowId);

      countsByRowId.set(rowId, {
         targetRowId: rowId,
         commentCount: (currentCount?.commentCount ?? 0) + 1,
         mentionCount: (currentCount?.mentionCount ?? 0) + mentionCount
      });

      this.stateSubject.next({
         ...currentState,
         countsByRowId
      });
   }

   reset(): void {
      ++this.requestVersion;

      this.activeTaskInstanceId = null;
      this.loadingTaskInstanceId = null;
      this.pendingDeltas.clear();

      this.stateSubject.next({
         ...INITIAL_STATE,
         countsByRowId: new Map<string, HtmlReportRowActivityCount>()
      });
   }

   private applyPendingDeltas(
      countsByRowId: Map<string, HtmlReportRowActivityCount>
   ): void {
      for (const [rowId, delta] of this.pendingDeltas) {
         const currentCount = countsByRowId.get(rowId);

         countsByRowId.set(rowId, {
            targetRowId: rowId,
            commentCount: (currentCount?.commentCount ?? 0) + delta.commentCount,
            mentionCount: (currentCount?.mentionCount ?? 0) + delta.mentionCount
         });
      }
   }
}
